'use client';

import Link from '@eliadmin/core/src/components/link';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';

import { TbCategoryFilled } from 'react-icons/tb';

import type {
  CategoriesEntity,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryCard } from '../categories/category-card';

export function HeaderMenu({ menu, categories }: { menu: Menu; categories: CategoriesEntity[] }) {
  const [showProducts, setShowProducts] = useState(false);

  const productsToggleRef = useRef<HTMLButtonElement>(null);
  const productsPanelRef = useRef<HTMLDivElement>(null);

  const pathname = usePathname();

  useEffect(() => {
    setShowProducts(false);
  }, [pathname]);

  useEffect(() => {
    if (!showProducts) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;

      if (
        productsToggleRef.current?.contains(target) ||
        productsPanelRef.current?.contains(target)
      ) {
        return;
      }

      setShowProducts(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowProducts(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showProducts]);

  return (
    <div className="hidden flex-1 items-center gap-6 lg:flex xl:gap-8">
      {/* Overlay (portal) */}
      {showProducts &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            aria-hidden="true"
            className="fixed inset-0 z-20 bg-black/30"
            onClick={() => setShowProducts(false)}
          />,
          document.body,
        )}

      {menu.fields.map((item, index) => {
        const isProducts = index === 0;

        if (isProducts) {
          return (
            <button
              key={item.name}
              ref={productsToggleRef}
              type="button"
              aria-expanded={showProducts}
              onClick={() => setShowProducts(prev => !prev)}
              className={cn(
                'relative z-40 flex h-12 items-center gap-2 rounded-[4px] px-[18px] text-sm font-semibold uppercase text-white transition-all ease-in-out',
                showProducts ? 'bg-primary' : 'bg-black',
              )}
            >
              <TbCategoryFilled size={24} />
              {item.name}
            </button>
          );
        }

        return (
          <Link
            key={item.name}
            href={getSafeHref(item?.path ?? '#')}
            onClick={() => setShowProducts(false)}
            className="relative z-40 text-sm font-semibold text-black transition-all lg:text-base"
          >
            {item.name}
          </Link>
        );
      })}

      <HeaderMenuProduct
        ref={productsPanelRef}
        categories={categories}
        showProducts={showProducts}
        onNavigate={() => setShowProducts(false)}
      />
    </div>
  );
}

const HeaderMenuProduct = forwardRef<
  HTMLDivElement,
  { categories: CategoriesEntity[]; showProducts: boolean; onNavigate: () => void }
>(function HeaderMenuProduct({ categories, showProducts, onNavigate }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        'absolute left-1/2 top-full mt-2.5 w-full -translate-x-1/2',
        'grid grid-cols-3 gap-6 rounded-[4px] bg-white p-6',
        'transition-all duration-200 ease-out',
        'pointer-events-none -translate-y-10 opacity-0',
        showProducts && 'pointer-events-auto translate-y-0 opacity-100',
        'z-40',
      )}
    >
      {categories.map(category => (
        <CategoryCard category={category} onClick={onNavigate} key={category.slug} />
      ))}
    </div>
  );
});
